import React, { useMemo, useContext, useState, useEffect } from "react";

import { Box, Stack, IconButton, Tooltip, Badge, Menu, MenuItem, ListItemIcon, ListItemText, Divider, Typography, Button, Drawer, List, ListItem, ListItemButton } from "@mui/material";
import Logo from "@/taskpane/components/logo/Logo";
import { useWindowSize } from "@uidotdev/usehooks";
import { isRunningInOutlook } from "@/taskpane/services/office";
import { useMatch, useNavigate } from "react-router-dom";
import { AuthContext } from "@/taskpane/contexts/auth/AuthContext";
import { useGetUserDataQuery } from "@/taskpane/services/user.hook";
import { useGetUserAttributesQuery } from "@/taskpane/services/auth.hook";
import { useGetFoldersQuery } from "@/taskpane/services/folders.hook";
import { getSubFolders } from "@/taskpane/services/folders";
import type { Office } from "@/taskpane/services/sso";
import type { SubFolderData } from "@/taskpane/types/folder";
import { enqueueSnackbar } from "notistack";
import NotificationsIcon from '@mui/icons-material/Notifications';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import HelpOutline from '@mui/icons-material/HelpOutline';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Logout from '@mui/icons-material/Logout';
import SwapHoriz from '@mui/icons-material/SwapHoriz';
import { EmailOutlined, CloseOutlined } from '@mui/icons-material';
import LaunchIcon from '@mui/icons-material/Launch';

export function Header() {
  const { height } = useWindowSize();
  const navigate = useNavigate();
  const { isAuthenticated, tokens, logout } = useContext(AuthContext);

  const isSmallScreen = useMemo(() => height ? height < 800 : false, [height]);

  const isOutlook = isRunningInOutlook();
  const isChat = useMatch("/folder/:folderId/:sousDossierId/chat");
  const isFolders = useMatch("/folders");
  
  // Pages where we should NOT show header buttons (login/auth pages)
  const isLoginPage = useMatch("/login");
  const isCallbackPage = useMatch("/auth/sso/callback");
  const isPollingPage = useMatch("/auth/sso/polling");
  const isOfficeSelectionPage = useMatch("/select-office");
  const isCrpcenPage = useMatch("/crpcen");
  
  // Show header buttons on all pages except login/auth pages
  const shouldShowHeaderButtons = isAuthenticated && !isLoginPage && !isCallbackPage && !isPollingPage && !isOfficeSelectionPage && !isCrpcenPage;

  // State for header buttons
  const [isChatOpen, setIsChatOpen] = useState<boolean>(false);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<null | HTMLElement>(null);
  const [availableOffices, setAvailableOffices] = useState<Office[]>([]);
  const [showFiliationChoiceButton, setShowFiliationChoiceButton] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sousDossierWithChoiceFiliationNeededList, setSousDossierWithChoiceFiliationNeededList] = useState<SubFolderData[]>([]);
  
  const isDevEnvironment = useMemo(() => {
    const apiUrl = process.env.REACT_APP_BASE_URL_API_ADDIN || '';
    return apiUrl.includes('dev');
  }, []);

  // Fetch user data
  const { data: userData } = useGetUserDataQuery({
    enabled: isAuthenticated && !!tokens?.IdToken,
    staleTime: 1000 * 60 * 10,
  });

  const hasValidAccessToken = Boolean(tokens?.AccessToken && tokens.AccessToken.length > 0);
  const { data: currentUser } = useGetUserAttributesQuery({
    enabled: hasValidAccessToken,
    staleTime: 1000 * 60 * 10,
  });

  // Fetch folders data for notification system
  const { data: foldersData } = useGetFoldersQuery({
    enabled: isAuthenticated && !!tokens?.IdToken,
    staleTime: 1000 * 60 * 10,
  });

  // Handlers
  const handleAccountMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAccountMenuAnchor(event.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAccountMenuAnchor(null);
  };

  const handleSwitchCRPCEN = (office: Office) => {
    handleAccountMenuClose();
    
    if (office.NumeroCRPCEN === currentUser?.crpcen) {
      return;
    }
    
    localStorage.setItem('target_office_tenant_id', office.TenantId);
    localStorage.setItem('target_office_name', office.Nom);
    // Store CRPCEN if available (some studies may not have it yet)
    localStorage.setItem('target_office_crpcen', office.NumeroCRPCEN || '');
    
    logout();
    
    enqueueSnackbar(`Changement vers ${office.Nom}. Authentification en cours...`, { variant: 'info' });
    navigate('/login');
  };

  const handleLogout = () => {
    handleAccountMenuClose();
    
    localStorage.removeItem('target_office_tenant_id');
    localStorage.removeItem('target_office_name');
    localStorage.removeItem('target_office_crpcen');
    sessionStorage.removeItem('sso_auto_triggered');
    
    logout();
    navigate('/login');
  };


  // Check folders for filiation notifications
  useEffect(() => {
    if (foldersData?.dossiers) {
      const folderWithFiliationStatus = foldersData.dossiers.find(
        (folder) => folder.en_attente_choix_filiation_dossier
      );
      
      const shouldShowButton = !!folderWithFiliationStatus;
      if (shouldShowButton !== showFiliationChoiceButton) {
        if (folderWithFiliationStatus) {
          // Update SousDossierWithChoiceFiliationNeededList by calling the API for each folder
          const allSousDossiersPromises = foldersData.dossiers
            .filter((folder) => folder.en_attente_choix_filiation_dossier)
            .map((folder) => 
              getSubFolders(folder.dossier_id).then((subFoldersData) => {
                if (subFoldersData?.sous_dossiers) {
                  return subFoldersData.sous_dossiers.filter(
                    (sousDossier) => sousDossier.en_attente_choix_filiation_sous_dossier
                  );
                }
                return [];
              })
            );

          Promise.all(allSousDossiersPromises).then((results) => {
            const allSousDossiers = results.flat();
            setSousDossierWithChoiceFiliationNeededList(allSousDossiers);
          });
        }
        setShowFiliationChoiceButton(shouldShowButton);
      }
    }
  }, [foldersData, showFiliationChoiceButton]);

  // Load available offices from localStorage
  useEffect(() => {
    try {
      const officesStr = localStorage.getItem('available_offices');
      if (officesStr) {
        const offices: Office[] = JSON.parse(officesStr);
        setAvailableOffices(offices);
      }
    } catch (error) {
      console.error('[Header] Error loading available offices:', error);
    }
  }, [currentUser?.crpcen]);

  const defaultStyles = {
    box: {
      display: "flex",
      flexDirection: "row",
      overflow: "hidden",
      margin: isFolders ? "none" : "0 auto",
      maxWidth: "1600px",
      width: "100%",
      zIndex: 0,
      pt: 1
    },
    logo: {
      height: "55px",
      margin: "0 auto",
    }
  };

  const smallStyles = {
    box: {
      display: "flex",
      flexDirection: "row",
      overflow: "hidden",
      margin: "0 auto",
      maxWidth: "600px",
      width: "100%",
      zIndex: 2,
      pt: "2px"
    },
    logo: {
      height: "55px",
      margin: "auto"
    }
  };

  if (isOutlook && isChat) {
    return null
  }

  if (!isOutlook && isChat) {
    return (
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          padding: 3,
        }}
      >
        <Logo height={smallStyles.logo.height}/>
      </Box>
    );
  }
  return (
    <Box
      sx={isSmallScreen ? smallStyles.box : defaultStyles.box}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%" sx={{ zIndex: 2, position: "relative", padding: 1 }}>
        <Logo height={isSmallScreen ? smallStyles.logo.height : defaultStyles.logo.height}/>
        
        {/* Header buttons - shown on all authenticated pages except login/auth pages */}
        {shouldShowHeaderButtons && (
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}>
            {/* Dashboard button - only for admin users */}
            {userData?.user_info?.role === "ADMIN" && (
              <Tooltip title="Accéder au tableau de bord">
                <IconButton 
                  onClick={() => navigate('/dashboard')} 
                  size="medium"
                >
                  <SpaceDashboardIcon 
                    color="primary" 
                    sx={{
                      width: { xs: 28, sm: 32 }, 
                      height: { xs: 28, sm: 32 },
                      display: { xs: 'none', sm: 'block' },
                      '@media (min-width: 315px)': {
                        display: 'block'
                      }
                    }}
                  />
                </IconButton>
              </Tooltip>
            )}

            {/* Help button */}
            {isDevEnvironment && (
              <Tooltip title="Besoin d'aide ?">
                <IconButton
                  size="medium"
                  onClick={() => {
                    if (!isAuthenticated) {
                      console.log("User not authenticated");
                      return;
                    }

                    const checkCrispReady = () => {
                      if (window.$crisp) {
                        if (isChatOpen) {
                          window.$crisp.push(["do", "chat:close"]);
                        } else {
                          window.$crisp.push(["do", "chat:show"]);
                          window.$crisp.push(["do", "chat:open"]);
                        }
                      } else {
                        setTimeout(checkCrispReady, 100);
                      }
                    };
                    checkCrispReady();
                  }}
                >
                  <HelpOutline sx={{ 
                    width: { xs: 28, sm: 32 }, 
                    height: { xs: 28, sm: 32 },
                    color: isChatOpen ? 'primary.main' : 'inherit',
                    display: { xs: 'none', sm: 'block' },
                    '@media (min-width: 260px)': {
                      display: 'block'
                    }
                  }} />
                </IconButton>
              </Tooltip>
            )}

            {/* Notifications button */}
            <Tooltip title="Dossier(s) en attente de sélection des biens">
              <span>
                <IconButton
                  size="medium"
                  onClick={() => setDrawerOpen(true)}
                  disabled={!showFiliationChoiceButton}
                >
                  {showFiliationChoiceButton ? (
                    <Badge 
                      variant="dot" 
                      color="warning" 
                    >
                      <NotificationsIcon sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 } }} />
                    </Badge>
                  ) : (
                    <NotificationsIcon sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 } }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>

            {/* Account menu button */}
            <Tooltip title="Mon compte">
              <IconButton
                size="medium"
                onClick={handleAccountMenuOpen}
              >
                <AccountCircle sx={{ width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 } }} />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={accountMenuAnchor}
              open={Boolean(accountMenuAnchor)}
              onClose={handleAccountMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              {/* Display all available offices/CRPCENs */}
              {availableOffices.length > 0 ? (
                <>
                  {availableOffices.map((office) => {
                    const isCurrent = office.NumeroCRPCEN === (currentUser?.crpcen || localStorage.getItem('selected_office_crpcen'));
                    return (
                      <MenuItem 
                        key={office.TenantId}
                        onClick={() => handleSwitchCRPCEN(office)}
                        selected={isCurrent}
                        sx={{
                          backgroundColor: isCurrent ? 'action.selected' : 'inherit',
                          '&:hover': {
                            backgroundColor: isCurrent ? 'action.selected' : 'action.hover',
                          }
                        }}
                      >
                        <ListItemIcon>
                          {isCurrent ? (
                            <SwapHoriz fontSize="small" color="primary" />
                          ) : (
                            <SwapHoriz fontSize="small" />
                          )}
                        </ListItemIcon>
                        <ListItemText 
                          primary={office.Nom}
                          secondary={`CRPCEN: ${office.NumeroCRPCEN}`}
                          primaryTypographyProps={{
                            fontWeight: isCurrent ? 600 : 400,
                            color: isCurrent ? 'primary' : 'inherit'
                          }}
                        />
                      </MenuItem>
                    );
                  })}
                  <Divider />
                </>
              ) : (
                <>
                  <MenuItem disabled>
                    <Typography variant="caption" color="textSecondary">
                      CRPCEN: {currentUser?.crpcen || localStorage.getItem('selected_office_crpcen') || 'N/A'}
                    </Typography>
                  </MenuItem>
                  <Divider />
                </>
              )}
              
              {/* Logout option */}
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                <ListItemText>Déconnexion</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Stack>

      {/* Notifications Drawer */}
      <Drawer 
        anchor='right' 
        open={drawerOpen} 
        onClose={() => setDrawerOpen(false)} 
        hideBackdrop
        PaperProps={{
          sx: { width: "90%", maxWidth: 400, backgroundColor: "rgb(255, 255, 255, 0.8)", borderTopLeftRadius: 4, borderTopRightRadius: 0, borderBottomLeftRadius: 4, borderBottomRightRadius: 0 },
        }}
      >
        <Box sx={{ width: '100%', backgroundColor: "transparent" }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}>
            <Typography color="primary" variant="h6" fontWeight="bold" sx={{fontSize: '14px' }}>
              Dossier(s) en attente de choix
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)} size="small">
              <CloseOutlined sx={{ "&:hover": { color: "error.main" } }} />
            </IconButton>
          </Box>

          <List dense disablePadding>
            {sousDossierWithChoiceFiliationNeededList?.map((sous_dossier) => (
              <ListItemButton 
                key={`sous-dossier-${sous_dossier.sous_dossier_id}-${sous_dossier.dossier_id}`} 
                divider 
                disableGutters 
                alignItems="flex-start" 
                dense 
                onClick={() => {
                  navigate(`/folder/${sous_dossier.dossier_id}/filiation/${sous_dossier.sous_dossier_id}`);
                  setDrawerOpen(false);
                }} 
                sx={{ "&:hover": { backgroundColor: "rgba(27,72,158,0.05)" } }}
              >
                <ListItem dense alignItems="flex-start" sx={{ backgroundColor: "transparent" }}
                  secondaryAction={
                    <IconButton edge="end" sx={{ color: "#1C252E" }}>
                      <LaunchIcon />
                    </IconButton>
                  }>
                  <ListItemText
                    primary={
                      <>
                        <Box sx={{ m: 0 }}>
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{
                              marginBottom: -0.5,
                              overflow: "hidden",
                              fontSize: "11px",
                              textOverflow: "ellipsis",
                              color: "#1C252E"
                            }}
                            title={sous_dossier.dossier_id}
                          >
                            {sous_dossier.dossier_id ?? ""}
                          </Typography>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              color: "#1C252E"
                            }}
                            title={sous_dossier.dossier_name}
                          >
                            {sous_dossier.dossier_name ?? ""}
                          </Typography>
                        </Box>
                      </>
                    }
                    secondary={
                      <Stack spacing={0.5}>
                        <Typography variant="body2" sx={{ color: '#637381', display: 'inline', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', fontSize: '12px' }}>
                          {sous_dossier.sous_dossier_name}
                        </Typography>
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{ color: '#637381', display: 'inline', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', fontSize: '12px' }}
                          title={sous_dossier.en_attente_choix_filiation_sous_dossier_by_details?.map(detail => detail.email).join(', ') || 'Aucun email'}
                        >
                          {sous_dossier.en_attente_choix_filiation_sous_dossier_by_details?.map(detail => detail.email.split('@')[0]).join(', ') || 'Aucun email'}
                        </Typography>
                      </Stack>
                    }
                  />
                </ListItem>
              </ListItemButton>
            ))}
          </List>
        </Box>
        <Box sx={{ backgroundColor: "rgb(255, 255, 255, 0.15)", backdropFilter: "blur(4px)", position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: -1, height: `calc(6.7rem * ${sousDossierWithChoiceFiliationNeededList?.length})` }}></Box>
      </Drawer>
    </Box>
  );
}
